import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BookOpen, Edit2, FileSpreadsheet, Plus, RefreshCw, Trash2 } from 'lucide-react'
import {
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  Select,
  Switch,
  Textarea,
  useToast,
  type ColumnDef,
} from '@/components/ui'
import {
  fetchQuoteStandards,
  createQuoteStandard,
  updateQuoteStandard,
  deleteQuoteStandard,
  syncQuoteStandard,
  syncAllQuoteStandards,
  analyzeExcelFile,
  importExcelFile,
  type QuoteStandard,
  type QuoteStandardInput,
  type SyncResult,
  type ExcelSheetPreview,
  type ExcelSheetSelection,
} from '@/api/quoteStandards'

// ─── Form state ───────────────────────────────────────────────────────────────

type FormState = {
  category: string
  title: string
  preferredVendor: string
  minSpecsJson: string
  defaultServiceItemCodes: string
  sapPreferredGroupCodes: string
  sourceType: 'manual' | 'confluence' | 'excel'
  confluencePageId: string
  confluenceSpaceKey: string
  active: boolean
}

const emptyForm = (): FormState => ({
  category: '',
  title: '',
  preferredVendor: '',
  minSpecsJson: '',
  defaultServiceItemCodes: '',
  sapPreferredGroupCodes: '',
  sourceType: 'manual',
  confluencePageId: '',
  confluenceSpaceKey: '',
  active: true,
})

function standardToForm(s: QuoteStandard): FormState {
  return {
    category: s.category,
    title: s.title,
    preferredVendor: s.preferredVendor ?? '',
    minSpecsJson: s.minSpecsJson ? JSON.stringify(s.minSpecsJson, null, 2) : '',
    defaultServiceItemCodes: s.defaultServiceItemCodes
      ? JSON.stringify(s.defaultServiceItemCodes, null, 2)
      : '',
    sapPreferredGroupCodes: s.sapPreferredGroupCodes
      ? JSON.stringify(s.sapPreferredGroupCodes, null, 2)
      : '',
    sourceType: s.sourceType,
    confluencePageId: s.confluencePageId ?? '',
    confluenceSpaceKey: s.confluenceSpaceKey ?? '',
    active: s.active,
  }
}

// ─── JSON validation ──────────────────────────────────────────────────────────

function validateJsonField(value: string, fieldName: string): string | null {
  if (!value.trim()) return null
  try {
    JSON.parse(value)
    return null
  } catch {
    return `${fieldName}: Ungültiges JSON`
  }
}

function collectJsonErrors(form: FormState): string[] {
  const errors: string[] = []
  const e1 = validateJsonField(form.minSpecsJson, 'Min-Specs')
  const e2 = validateJsonField(form.defaultServiceItemCodes, 'Standard-Artikel')
  const e3 = validateJsonField(form.sapPreferredGroupCodes, 'SAP-Gruppen')
  if (e1) errors.push(e1)
  if (e2) errors.push(e2)
  if (e3) errors.push(e3)
  return errors
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatSyncDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return (
    d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
  )
}

function extractErrorMessage(err: unknown): string {
  return (
    (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data
      ?.error ??
    (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data
      ?.message ??
    (err as Error)?.message ??
    'Unbekannter Fehler'
  )
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[äÄ]/g, 'ae').replace(/[öÖ]/g, 'oe').replace(/[üÜ]/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

// ─── Sync error details ───────────────────────────────────────────────────────

function SyncErrorDetails({ results }: { results: SyncResult[] }) {
  const [open, setOpen] = useState(false)
  const failed = results.filter((r) => !r.ok)
  if (failed.length === 0) return null

  return (
    <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
      <div className="flex items-center justify-between">
        <span className="font-medium">
          {failed.length} Standard{failed.length > 1 ? 's' : ''} konnten nicht synchronisiert
          werden.
        </span>
        <button
          type="button"
          className="underline text-red-700 text-xs ml-4"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? 'Details ausblenden' : 'Details anzeigen'}
        </button>
      </div>
      {open && (
        <ul className="mt-2 space-y-1 list-disc list-inside">
          {failed.map((r) => (
            <li key={r.id}>
              <span className="font-mono">{r.category}</span> — {r.error ?? 'Unbekannter Fehler'}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ─── Column definitions ───────────────────────────────────────────────────────

function buildColumns(
  onEdit: (s: QuoteStandard) => void,
  onDelete: (s: QuoteStandard) => void,
  onSync: (s: QuoteStandard) => void,
  syncingId: string | null,
): ColumnDef<QuoteStandard>[] {
  return [
    {
      accessorKey: 'category',
      header: 'Kategorie',
      cell: ({ row }) => (
        <span className="font-mono text-sm text-gray-700">{row.original.category}</span>
      ),
    },
    {
      accessorKey: 'title',
      header: 'Titel',
    },
    {
      accessorKey: 'preferredVendor',
      header: 'Hersteller',
      cell: ({ row }) => row.original.preferredVendor ?? <span className="text-gray-400">—</span>,
    },
    {
      accessorKey: 'sourceType',
      header: 'Quelle',
      cell: ({ row }) => {
        const s = row.original
        if (s.sourceType === 'confluence') {
          return (
            <span className="text-sm text-blue-700 font-medium">
              Confluence{s.confluencePageId ? ` (${s.confluencePageId})` : ''}
            </span>
          )
        }
        if (s.sourceType === 'excel') {
          return <span className="text-sm text-emerald-700 font-medium">Excel</span>
        }
        return <span className="text-sm text-gray-500">Manuell</span>
      },
    },
    {
      accessorKey: 'lastSyncedAt',
      header: 'Letzter Sync',
      cell: ({ row }) => {
        const s = row.original
        if (s.sourceType !== 'confluence') return <span className="text-gray-400">—</span>
        return (
          <span className={s.lastSyncedAt ? 'text-gray-700' : 'text-orange-500'}>
            {s.lastSyncedAt ? formatSyncDate(s.lastSyncedAt) : 'Noch nicht synchronisiert'}
          </span>
        )
      },
    },
    {
      accessorKey: 'active',
      header: 'Aktiv',
      cell: ({ row }) => (
        <span className={row.original.active ? 'text-green-600 font-medium' : 'text-gray-400'}>
          {row.original.active ? 'Ja' : 'Nein'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => {
        const s = row.original
        const isSyncing = syncingId === s.id
        return (
          <div className="flex items-center justify-end gap-1">
            {s.sourceType === 'confluence' && s.confluencePageId && (
              <IconButton
                icon={<RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />}
                aria-label="Jetzt synchronisieren"
                size="sm"
                disabled={isSyncing}
                onClick={() => onSync(s)}
              />
            )}
            <IconButton
              icon={<Edit2 className="w-4 h-4" />}
              aria-label="Standard bearbeiten"
              size="sm"
              onClick={() => onEdit(s)}
            />
            <IconButton
              icon={<Trash2 className="w-4 h-4" />}
              aria-label="Standard löschen"
              size="sm"
              onClick={() => onDelete(s)}
            />
          </div>
        )
      },
    },
  ]
}

// ─── Excel sheet config ───────────────────────────────────────────────────────

interface SheetConfig {
  selected: boolean
  category: string
  title: string
  preferredVendor: string
}

// ─── Editor Modal ─────────────────────────────────────────────────────────────

interface EditorModalProps {
  open: boolean
  editing: QuoteStandard | null
  onClose: () => void
  onSave: (input: QuoteStandardInput) => Promise<void>
  onImported: (count: number) => void
  isSaving: boolean
}

const sourceTypeOptions = [
  { value: 'manual' as const, label: 'Manuell' },
  { value: 'confluence' as const, label: 'Confluence' },
  { value: 'excel' as const, label: 'Excel (Preisliste)' },
]

function EditorModal({ open, editing, onClose, onSave, onImported, isSaving }: EditorModalProps) {
  const [form, setForm] = useState<FormState>(emptyForm())
  const [jsonErrors, setJsonErrors] = useState<string[]>([])

  // Excel state
  const [excelFile, setExcelFile] = useState<File | null>(null)
  const [excelSheets, setExcelSheets] = useState<ExcelSheetPreview[]>([])
  const [excelConfigs, setExcelConfigs] = useState<Record<string, SheetConfig>>({})
  const [excelAnalyzing, setExcelAnalyzing] = useState(false)
  const [excelError, setExcelError] = useState<string | null>(null)
  const [excelImporting, setExcelImporting] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setForm(editing ? standardToForm(editing) : emptyForm())
      setJsonErrors([])
      setExcelFile(null)
      setExcelSheets([])
      setExcelConfigs({})
      setExcelError(null)
      setExcelImporting(false)
    }
  }, [open, editing])

  const set = (field: keyof FormState, value: string | boolean) =>
    setForm((prev: FormState) => ({ ...prev, [field]: value }))

  // ─── Excel file handling ──────────────────────────────────────────────────

  const handleExcelFile = useCallback(async (f: File) => {
    setExcelFile(f)
    setExcelAnalyzing(true)
    setExcelError(null)
    try {
      const result = await analyzeExcelFile(f)
      setExcelSheets(result)
      const initial: Record<string, SheetConfig> = {}
      for (const s of result) {
        initial[s.name] = {
          selected: s.name !== 'Preisliste',
          category: slugify(s.name),
          title: s.name,
          preferredVendor: '',
        }
      }
      setExcelConfigs(initial)
    } catch (err) {
      setExcelError((err as Error)?.message ?? 'Fehler beim Lesen der Datei')
      setExcelFile(null)
    } finally {
      setExcelAnalyzing(false)
    }
  }, [])

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setDragOver(false)
      const f = e.dataTransfer.files[0]
      if (f) handleExcelFile(f)
    },
    [handleExcelFile],
  )

  const updateSheetConfig = (name: string, patch: Partial<SheetConfig>) => {
    setExcelConfigs((prev) => ({ ...prev, [name]: { ...prev[name]!, ...patch } }))
  }

  const selectedSheetCount = Object.values(excelConfigs).filter((c) => c.selected).length

  // ─── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (form.sourceType === 'excel' && !editing) {
      if (!excelFile || excelSheets.length === 0) {
        setExcelError('Bitte zuerst eine Excel-Datei hochladen.')
        return
      }
      if (selectedSheetCount === 0) {
        setExcelError('Bitte mindestens ein Tabellenblatt auswählen.')
        return
      }
      setExcelImporting(true)
      setExcelError(null)
      try {
        const selections: ExcelSheetSelection[] = excelSheets
          .filter((s) => excelConfigs[s.name]?.selected)
          .map((s) => ({
            sheetName: s.name,
            category: excelConfigs[s.name]!.category,
            title: excelConfigs[s.name]!.title,
            preferredVendor: excelConfigs[s.name]!.preferredVendor || undefined,
          }))
        const created = await importExcelFile(excelFile, selections)
        onImported(created.length)
        onClose()
      } catch (err) {
        setExcelError((err as Error)?.message ?? 'Fehler beim Importieren')
        setExcelImporting(false)
      }
      return
    }

    const errors = collectJsonErrors(form)
    if (errors.length > 0) {
      setJsonErrors(errors)
      return
    }
    setJsonErrors([])
    await onSave(form)
  }

  const isConfluence = form.sourceType === 'confluence'
  const isExcel = form.sourceType === 'excel'
  const isExcelNew = isExcel && !editing
  const isExcelEdit = isExcel && !!editing
  const isBusy = isSaving || excelAnalyzing || excelImporting

  const saveLabel = isExcelNew
    ? selectedSheetCount > 0
      ? `${selectedSheetCount} Blatt/Blätter importieren`
      : 'Kein Blatt ausgewählt'
    : 'Speichern'

  return (
    <Modal
      open={open}
      onOpenChange={(next) => { if (!next) onClose() }}
      title={editing ? 'Standard bearbeiten' : 'Neuer Standard'}
      size={isExcelNew && excelSheets.length > 0 ? 'xl' : 'lg'}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={isBusy}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={isBusy}
            disabled={isBusy || (isExcelNew && selectedSheetCount === 0 && excelSheets.length > 0)}
          >
            {saveLabel}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* JSON errors (non-excel) */}
        {jsonErrors.length > 0 && (
          <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            <p className="font-medium mb-1">JSON-Fehler — bitte korrigieren:</p>
            <ul className="list-disc list-inside space-y-0.5">
              {jsonErrors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Source type selector */}
        <Select
          label="Quelle"
          value={form.sourceType}
          options={sourceTypeOptions}
          onValueChange={(v) => {
            set('sourceType', v)
            setExcelFile(null)
            setExcelSheets([])
            setExcelConfigs({})
            setExcelError(null)
          }}
          disabled={isBusy || !!editing}
          hint={
            isExcelNew
              ? 'Excel-Preisliste hochladen — jedes ausgewählte Tabellenblatt wird als eigener Standard gespeichert.'
              : isExcelEdit
                ? 'Excel-Standards können nicht nachbearbeitet werden. Löschen und neu importieren.'
                : 'Manuell: Inhalt direkt gepflegt. Confluence: Inhalt aus dem Wiki.'
          }
        />

        {/* ── Excel: new import flow ── */}
        {isExcelNew && (
          <div className="space-y-4">
            {excelError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {excelError}
              </div>
            )}

            {/* File picker */}
            {!excelFile && (
              <div
                className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-8 py-10 transition-colors cursor-pointer ${
                  dragOver
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-300 bg-gray-50 hover:border-brand-400 hover:bg-gray-100'
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <FileSpreadsheet className="w-10 h-10 text-gray-400" />
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-700">Excel-Datei hierher ziehen</p>
                  <p className="text-xs text-gray-500 mt-1">oder klicken zum Auswählen (.xlsx)</p>
                </div>
                {excelAnalyzing && (
                  <p className="text-sm text-brand-600 animate-pulse">Datei wird analysiert…</p>
                )}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleExcelFile(f) }}
            />

            {/* Sheet selection */}
            {excelFile && excelSheets.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-emerald-800">
                    <FileSpreadsheet className="w-4 h-4 shrink-0 text-emerald-600" />
                    <span className="font-medium">{excelFile.name}</span>
                    <span className="text-emerald-600">· {excelSheets.length} Blätter</span>
                  </div>
                  <button
                    type="button"
                    className="text-xs text-gray-500 underline"
                    onClick={() => { setExcelFile(null); setExcelSheets([]); setExcelConfigs({}) }}
                  >
                    Andere Datei
                  </button>
                </div>

                <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                  {excelSheets.map((sheet) => {
                    const cfg = excelConfigs[sheet.name]
                    if (!cfg) return null
                    return (
                      <div
                        key={sheet.name}
                        className={`rounded-lg border p-3 space-y-2 transition-colors ${
                          cfg.selected ? 'border-brand-300 bg-brand-50/30' : 'border-gray-200 bg-gray-50/50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={cfg.selected}
                            onChange={(e) => updateSheetConfig(sheet.name, { selected: e.target.checked })}
                            disabled={excelImporting}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800">{sheet.name}</p>
                            <p className="text-xs text-gray-500">{sheet.rowCount} Zeilen · {sheet.colCount} Spalten</p>
                          </div>
                        </div>

                        {cfg.selected && (
                          <div className="grid grid-cols-3 gap-2 pl-7">
                            <Input
                              label="Kategorie"
                              value={cfg.category}
                              onChange={(e) => updateSheetConfig(sheet.name, { category: slugify(e.target.value) || cfg.category })}
                              disabled={excelImporting}
                              placeholder="enreach-contact"
                            />
                            <Input
                              label="Titel"
                              value={cfg.title}
                              onChange={(e) => updateSheetConfig(sheet.name, { title: e.target.value })}
                              disabled={excelImporting}
                              placeholder="Enreach Contact"
                            />
                            <Input
                              label="Hersteller"
                              value={cfg.preferredVendor}
                              onChange={(e) => updateSheetConfig(sheet.name, { preferredVendor: e.target.value })}
                              disabled={excelImporting}
                              placeholder="z.B. Enreach"
                            />
                          </div>
                        )}

                        {cfg.selected && sheet.preview.length > 0 && (
                          <div className="pl-7 overflow-x-auto">
                            <table className="text-xs border-collapse">
                              <tbody>
                                {sheet.preview.slice(0, 3).map((row, ri) => (
                                  <tr key={ri} className={ri === 0 ? 'bg-gray-100 font-medium' : ''}>
                                    {row.map((cell, ci) => (
                                      <td
                                        key={ci}
                                        className="border border-gray-200 px-2 py-0.5 whitespace-nowrap max-w-[140px] truncate"
                                        title={cell}
                                      >
                                        {cell}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Excel: editing existing (read-only info) ── */}
        {isExcelEdit && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Excel-Standards können nicht neu hochgeladen werden. Löschen Sie diesen Eintrag und importieren Sie die Datei erneut, um die Preisliste zu aktualisieren.
          </div>
        )}

        {/* ── Normal fields (manual / confluence / excel-edit) ── */}
        {(!isExcelNew) && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Kategorie"
                hint='Kleinbuchstaben, Zahlen, Bindestriche — z.B. "laptop-office"'
                value={form.category}
                onChange={(e) => set('category', e.target.value)}
                disabled={isBusy}
                placeholder="laptop-office"
              />
              <Input
                label="Titel"
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                disabled={isBusy}
                placeholder="Office-Laptop Standard"
              />
            </div>

            <Input
              label="Bevorzugter Hersteller"
              value={form.preferredVendor}
              onChange={(e) => set('preferredVendor', e.target.value)}
              disabled={isBusy}
              placeholder="z.B. Lenovo"
            />
          </>
        )}

        {/* ── Confluence fields ── */}
        {isConfluence && (
          <div className="grid grid-cols-2 gap-4 rounded-md border border-blue-100 bg-blue-50 p-4">
            <Input
              label="Confluence Page-ID"
              hint="Numerische ID aus der Confluence-URL (z.B. 12345678)"
              value={form.confluencePageId}
              onChange={(e) => set('confluencePageId', e.target.value)}
              disabled={isBusy}
              placeholder="12345678"
            />
            <Input
              label="Space-Key (optional)"
              hint="Confluence-Space-Kürzel, z.B. IT oder WIKI"
              value={form.confluenceSpaceKey}
              onChange={(e) => set('confluenceSpaceKey', e.target.value)}
              disabled={isBusy}
              placeholder="IT"
            />
          </div>
        )}

        {/* ── JSON fields (only for manual / confluence) ── */}
        {!isExcel && (
          <>
            <Textarea
              label="Min-Specs (JSON-Objekt)"
              hint='Freies JSON-Objekt, z.B. {"ramGb":16,"cpuMin":"i5"}'
              value={form.minSpecsJson}
              onChange={(e) => set('minSpecsJson', e.target.value)}
              disabled={isBusy}
              rows={4}
              placeholder={'{\n  "ramGb": 16,\n  "cpuMin": "i5"\n}'}
            />

            <Textarea
              label="Standard-Artikel (JSON-Array mit SAP-Item-Codes)"
              hint='z.B. ["SVCSETUP01","WAREXT3Y"]'
              value={form.defaultServiceItemCodes}
              onChange={(e) => set('defaultServiceItemCodes', e.target.value)}
              disabled={isBusy}
              rows={3}
              placeholder={'["SVCSETUP01", "WAREXT3Y"]'}
            />

            <Textarea
              label="Bevorzugte Warengruppen (SAP) (JSON-Array)"
              hint='z.B. ["100","101"] — SAP-GroupCode-Nummern'
              value={form.sapPreferredGroupCodes}
              onChange={(e) => set('sapPreferredGroupCodes', e.target.value)}
              disabled={isBusy}
              rows={2}
              placeholder={'["100", "101"]'}
            />
          </>
        )}

        {/* ── Active toggle (not for new excel import) ── */}
        {!isExcelNew && (
          <Switch
            label="Aktiv"
            checked={form.active}
            onChange={(e) => set('active', e.target.checked)}
            disabled={isBusy}
          />
        )}
      </div>
    </Modal>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function StandardsAdminPage() {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [editorOpen, setEditorOpen] = useState(false)
  const [editing, setEditing] = useState<QuoteStandard | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<QuoteStandard | null>(null)
  const [syncingId, setSyncingId] = useState<string | null>(null)
  const [syncAllError, setSyncAllError] = useState<SyncResult[] | null>(null)

  const {
    data: standards = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['quote-standards'],
    queryFn: () => fetchQuoteStandards(),
  })

  const saveMutation = useMutation({
    mutationFn: async (input: QuoteStandardInput) => {
      if (editing) return updateQuoteStandard(editing.id, input)
      return createQuoteStandard(input)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote-standards'] })
      toast({ title: editing ? 'Standard aktualisiert' : 'Standard angelegt', tone: 'success' })
      setEditorOpen(false)
      setEditing(null)
    },
    onError: (err: unknown) => {
      toast({ title: 'Fehler beim Speichern', description: extractErrorMessage(err), tone: 'danger' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteQuoteStandard(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote-standards'] })
      toast({ title: 'Standard gelöscht', tone: 'success' })
      setDeleteTarget(null)
    },
    onError: (err: unknown) => {
      toast({ title: 'Fehler beim Löschen', description: extractErrorMessage(err), tone: 'danger' })
      setDeleteTarget(null)
    },
  })

  const handleSync = async (s: QuoteStandard) => {
    setSyncingId(s.id)
    try {
      await syncQuoteStandard(s.id)
      queryClient.invalidateQueries({ queryKey: ['quote-standards'] })
      toast({ title: `"${s.title}" synchronisiert`, tone: 'success' })
    } catch (err) {
      toast({
        title: 'Sync fehlgeschlagen',
        description: extractErrorMessage(err),
        tone: 'danger',
      })
    } finally {
      setSyncingId(null)
    }
  }

  const syncAllMutation = useMutation({
    mutationFn: () => syncAllQuoteStandards(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['quote-standards'] })
      if (result.summary.failed === 0) {
        toast({
          title: `${result.summary.ok} Standard${result.summary.ok !== 1 ? 's' : ''} synchronisiert`,
          tone: 'success',
        })
        setSyncAllError(null)
      } else {
        setSyncAllError(result.data)
        toast({
          title: `${result.summary.ok} synchronisiert, ${result.summary.failed} fehlgeschlagen`,
          tone: 'warning',
        })
      }
    },
    onError: (err: unknown) => {
      toast({ title: 'Sync-All fehlgeschlagen', description: extractErrorMessage(err), tone: 'danger' })
    },
  })

  const confluenceStandardsCount = standards.filter(
    (s) => s.sourceType === 'confluence' && s.confluencePageId,
  ).length

  const openCreate = () => {
    setEditing(null)
    setEditorOpen(true)
  }

  const openEdit = (s: QuoteStandard) => {
    setEditing(s)
    setEditorOpen(true)
  }

  const columns = buildColumns(openEdit, (s) => setDeleteTarget(s), handleSync, syncingId)

  return (
    <div className="space-y-4">
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <BookOpen className="w-6 h-6 text-brand-700" />
            Hardware- &amp; Hersteller-Standards
          </span>
        }
        breadcrumbs={[
          { label: 'Aufträge & Angebote', to: '/auftraege' },
          { label: 'Standards verwalten' },
        ]}
        actions={
          <div className="flex items-center gap-2">
            {confluenceStandardsCount > 0 && (
              <Button
                variant="secondary"
                leadingIcon={
                  <RefreshCw className={syncAllMutation.isPending ? 'animate-spin' : ''} />
                }
                onClick={() => syncAllMutation.mutate()}
                loading={syncAllMutation.isPending}
              >
                Alle synchronisieren ({confluenceStandardsCount})
              </Button>
            )}
            <Button variant="primary" leadingIcon={<Plus />} onClick={openCreate}>
              Neuer Standard
            </Button>
          </div>
        }
      />

      {syncAllError && <SyncErrorDetails results={syncAllError} />}

      <Card>
        <Card.Body>
          {isLoading ? (
            <LoadingState label="Standards werden geladen…" />
          ) : error ? (
            <ErrorState
              title="Fehler beim Laden"
              description={(error as Error)?.message}
              details={String(error)}
            />
          ) : standards.length === 0 ? (
            <EmptyState
              icon={<BookOpen />}
              title="Noch keine Standards gepflegt"
              description="Legen Sie Ihren ersten Hardware- oder Hersteller-Standard an, um ihn im KI-Angebotsdialog nutzen zu können."
              action={
                <Button variant="primary" leadingIcon={<Plus />} onClick={openCreate}>
                  Ersten Standard anlegen
                </Button>
              }
            />
          ) : (
            <DataTable
              data={standards}
              columns={columns}
              pagination={{ pageSize: 20 }}
            />
          )}
        </Card.Body>
      </Card>

      <EditorModal
        open={editorOpen}
        editing={editing}
        onClose={() => {
          setEditorOpen(false)
          setEditing(null)
        }}
        onSave={async (input) => {
          await saveMutation.mutateAsync(input)
        }}
        onImported={(count) => {
          queryClient.invalidateQueries({ queryKey: ['quote-standards'] })
          toast({
            title: `${count} Standard${count !== 1 ? 's' : ''} aus Excel importiert`,
            tone: 'success',
          })
          setEditorOpen(false)
          setEditing(null)
        }}
        isSaving={saveMutation.isPending}
      />

      {deleteTarget && (
        <ConfirmDialog
          open={!!deleteTarget}
          title="Standard löschen?"
          message={`"${deleteTarget.title}" (${deleteTarget.category}) wird unwiderruflich gelöscht.`}
          confirmLabel="Löschen"
          variant="danger"
          loading={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  )
}
